"use client"

import { useEffect } from "react"
import {
  consumeChunkReloadRecovery,
  isChunkLoadError,
  reloadOnceForChunkError,
} from "@/lib/chunk-error"
import { trackEvent } from "@/lib/analytics"

/**
 * Recovers users who landed on a stale tab after a deploy, where dynamic
 * imports reference chunk filenames that have been rotated and 404. Most
 * cases are caught by the ErrorBoundary's componentDidCatch; this picks up
 * the rest (unhandled rejections from `import()`, plain window errors).
 */
export function ChunkErrorReloader() {
  useEffect(() => {
    const recovery = consumeChunkReloadRecovery()
    const recoveryTimer = recovery
      ? window.setTimeout(() => {
          trackEvent("chunk_reload_recovered", {
            error_source: recovery.source,
            attempted_path: recovery.path,
            elapsed_ms: recovery.elapsedMs,
            recovery_method: "cache_busted_reload",
          })
        }, 0)
      : undefined

    const onError = (event: ErrorEvent) => {
      if (isChunkLoadError(event.error) || isChunkLoadError(event)) {
        reloadOnceForChunkError("window_error")
      }
    }
    const onRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        reloadOnceForChunkError("unhandled_rejection")
      }
    }
    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onRejection)
    return () => {
      if (recoveryTimer !== undefined) window.clearTimeout(recoveryTimer)
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onRejection)
    }
  }, [])

  return null
}
